'use client';

import Link from 'next/link';
import Image from 'next/image';
import { type Locale, getLocalePath } from '@/lib/i18n';
import { ROUTES } from '@/lib/routes';
import styles from './SolutionsGrid.module.css';

interface Solution {
  routeKey: string; // 'kiosk' | 'qr' | 'tablet' | 'webshop'
  label: string;
  description: string;
  badge: string;
  image?: string;
}

interface SolutionsGridProps {
  label?: string;
  title: string;
  subtitle?: string;
  solutions: Solution[];
  locale: Locale;
}

// Default images per solution type
const DEFAULT_IMAGES: Record<string, string> = {
  // QR Solutions
  'QR': '/images/mockups/qr-mockup.webp',
  'QR Bestellen': '/images/mockups/qr-mockup.webp',
  'QR Ordering': '/images/mockups/qr-mockup.webp',
  'QR-Bestellung': '/images/mockups/qr-mockup.webp',
  
  // Tablet Solutions
  'Tablet': '/images/mockups/tablet-mockup.webp',
  'Tablet Bestellen': '/images/mockups/tablet-mockup.webp',
  'Tablet Ordering': '/images/mockups/tablet-mockup.webp',
  'Tablet-Bestellung': '/images/mockups/tablet-mockup.webp',
  
  // Kiosk Solutions
  'Kiosk': '/images/mockups/kiosk-mockup.webp',
  'Horeca Kiosk': '/images/mockups/kiosk-mockup.webp',
  'Self-Ordering Kiosk': '/images/mockups/kiosk-mockup.webp',
  'Bestellterminal': '/images/mockups/kiosk-mockup.webp',
  
  // Webshop Solutions
  'Webshop': '/images/mockups/webshop-mockup.webp',
  'Online': '/images/mockups/webshop-mockup.webp',
  'Online Webshop': '/images/mockups/webshop-mockup.webp',
  'Online Ordering': '/images/mockups/webshop-mockup.webp',
  'Online-Bestellung': '/images/mockups/webshop-mockup.webp',
};

// Get image based on solution label
function getImageForSolution(label: string, customImage?: string): string {
  if (customImage) return customImage;
  
  // Try exact match first
  if (DEFAULT_IMAGES[label]) return DEFAULT_IMAGES[label];
  
  // Find matching key (partial match)
  for (const [key, value] of Object.entries(DEFAULT_IMAGES)) {
    if (label.includes(key) || key.includes(label)) return value;
  }
  
  return '/images/mockups/kiosk-mockup.webp'; // fallback
}

export function SolutionsGrid({
  label,
  title,
  subtitle,
  solutions,
  locale
}: SolutionsGridProps) {
  return (
    <section className={styles.solutionsGrid}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          {label && <span className={styles.label}>{label}</span>}
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>

        {/* Solutions Grid */}
        <div className={styles.grid}>
          {solutions.map((solution, index) => {
            // Get URL from ROUTES based on routeKey
            const route = (ROUTES as any)[solution.routeKey]?.[locale];
            if (!route) return null; // Skip if route not found
            
            const href = getLocalePath(locale, route);
            const imageUrl = getImageForSolution(solution.label, solution.image);
            
            return (
              <Link 
                key={solution.routeKey}
                href={href}
                className={styles.card}
              >
                {/* Badge */}
                <div className={styles.badge}>{solution.badge}</div>
                
                {/* Image Container */}
                <div className={styles.imageWrapper}>
                  <Image
                    src={imageUrl}
                    alt={`${solution.label} - Jamezz`}
                    width={300}
                    height={300}
                    className={styles.image}
                  />
                </div>
                
                {/* Content */}
                <div className={styles.content}>
                  <h3 className={styles.cardTitle}>{solution.label}</h3>
                  <p className={styles.cardDescription}>{solution.description}</p>
                  
                  {/* Arrow Icon with Gradient */}
                  <div className={styles.arrow}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <defs>
                        <linearGradient id={`arrowGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#FF4D4F" />
                          <stop offset="100%" stopColor="#FF8C8C" />
                        </linearGradient>
                      </defs>
                      <path 
                        d="M5 12h14m-7-7l7 7-7 7" 
                        stroke={`url(#arrowGradient-${index})`}
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
